import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ICE_SERVERS } from '../../utils/webrtc-config';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalService } from '../../services/global.service';
import { LiveProctorService } from '../../services/live-proctor.service';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-live-proctor-detail-page',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent],
  templateUrl: './live-proctor-detail-page.component.html',
})
export class LiveProctorDetailPageComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('mainVideoEl') mainVideoEl: ElementRef<HTMLVideoElement> | undefined;
  @ViewChild('fullscreenVideoEl') fullscreenVideoEl: ElementRef<HTMLVideoElement> | undefined;
  @ViewChild('carouselEl') carouselEl: ElementRef<HTMLDivElement> | undefined;

  evaluator_id: number = 0;
  selected_tab: 'info' | 'ai' | 'activity' | 'messenger' = 'info';
  evaluator: any = null;
  ai_triggers: any[] = [];
  activity_logs: any[] = [];
  messages: any[] = [];
  carousel_items: any[] = [];
  new_message = '';
  is_fullscreen = false;

  private ws: WebSocket | null = null;
  private peer_connection: RTCPeerConnection | null = null;
  remote_stream: MediaStream | null = null;
  admin_user_id: number = 0;
  stream_connected = false;

  private stream_set_on_main = false;
  private stream_set_on_full = false;
  private carousel_peer_connections: Map<number, RTCPeerConnection> = new Map();

  constructor(
    public gs: GlobalService,
    public lps: LiveProctorService,
    public hs: HttpService,
    public router: Router,
    public ar: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) { }

  async ngOnInit() {
    this.admin_user_id = this.gs.user_data?.id || 0;
    this.ar.params.subscribe(async (params) => {
      this.evaluator_id = +params['id'];
      this.reset_state();
      await this.load_detail();
      this.connect_websocket();
    });
  }

  reset_state() {
    this.evaluator = null;
    this.ai_triggers = [];
    this.activity_logs = [];
    this.messages = [];
    this.carousel_items = [];
    this.remote_stream = null;
    this.stream_connected = false;
    this.stream_set_on_main = false;
    this.stream_set_on_full = false;
    this.selected_tab = 'info';
    if (this.peer_connection) {
      this.peer_connection.close();
      this.peer_connection = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.carousel_peer_connections.forEach(pc => pc.close());
    this.carousel_peer_connections.clear();
  }

  ngOnDestroy() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.peer_connection) {
      this.peer_connection.close();
      this.peer_connection = null;
    }
    if (this.remote_stream) {
      this.remote_stream.getTracks().forEach(t => t.stop());
      this.remote_stream = null;
    }
    this.carousel_peer_connections.forEach(pc => pc.close());
    this.carousel_peer_connections.clear();
  }

  ngAfterViewChecked() {
    if (this.remote_stream && !this.stream_set_on_main && this.mainVideoEl?.nativeElement) {
      this.mainVideoEl.nativeElement.srcObject = this.remote_stream;
      this.stream_set_on_main = true;
    }
    if (this.is_fullscreen && this.remote_stream && !this.stream_set_on_full && this.fullscreenVideoEl?.nativeElement) {
      this.fullscreenVideoEl.nativeElement.srcObject = this.remote_stream;
      this.stream_set_on_full = true;
    }
  }

  connect_websocket() {
    const base_ws = this.hs.base_url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const ws_protocol = this.hs.base_url.startsWith('https') ? 'wss' : 'ws';
    const ws_url = `${ws_protocol}://${base_ws}/ws/live-proctor`;
    console.log('[Proctor-Admin] Connecting WS:', ws_url);
    this.ws = new WebSocket(ws_url);

    this.ws.onopen = () => {
      console.log('[Proctor-Admin] WS open, admin_user_id:', this.admin_user_id);
      this.ws?.send(JSON.stringify({ type: 'join_admin', user_id: this.admin_user_id }));
      setTimeout(() => this.request_stream(this.evaluator_id), 200);
      setTimeout(() => this.request_carousel_streams(), 400);
    };

    this.ws.onmessage = async (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log('[Proctor-Admin] WS message:', msg.type, msg);
        if (msg.type === 'answer') {
          const from_id = Number(msg.from_user_id || msg.user_id || 0);
          if (from_id && from_id !== this.evaluator_id && this.carousel_peer_connections.has(from_id)) {
            await this.carousel_peer_connections.get(from_id)!.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          } else {
            await this.handle_answer(msg.sdp);
          }
        } else if (msg.type === 'ice_candidate') {
          const from_id = Number(msg.from_user_id || msg.user_id || 0);
          if (from_id && from_id !== this.evaluator_id && this.carousel_peer_connections.has(from_id)) {
            if (msg.candidate) await this.carousel_peer_connections.get(from_id)!.addIceCandidate(new RTCIceCandidate(msg.candidate));
          } else if (this.peer_connection && msg.candidate) {
            await this.peer_connection.addIceCandidate(new RTCIceCandidate(msg.candidate));
          }
        } else if (msg.type === 'evaluator_online' && String(msg.user_id) === String(this.evaluator_id)) {
          // Evaluator (re)connected after admin — re-send offer
          setTimeout(() => this.request_stream(this.evaluator_id), 300);
        }
      } catch (e) { console.error('[Proctor-Admin] WS message error:', e); }
    };

    this.ws.onerror = (e) => console.error('[Proctor-Admin] WS error:', e);
    this.ws.onclose = (e) => console.warn('[Proctor-Admin] WS closed:', e.code, e.reason);
  }

  async request_stream(user_id: number) {
    console.log('[Proctor-Admin] request_stream for user_id:', user_id, 'ws readyState:', this.ws?.readyState);
    if (this.peer_connection) {
      this.peer_connection.close();
      this.peer_connection = null;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.peer_connection = pc;

    pc.ontrack = (event) => {
      console.log('[Proctor-Admin] ontrack fired! streams:', event.streams.length);
      if (event.streams && event.streams[0]) {
        this.remote_stream = event.streams[0];
        this.stream_connected = true;
        this.stream_set_on_main = false;
        this.stream_set_on_full = false;
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.ws?.send(JSON.stringify({ type: 'ice_candidate', candidate: e.candidate, target_type: 'evaluator', target_user_id: user_id, from_user_id: this.admin_user_id }));
      }
    };

    pc.oniceconnectionstatechange = () => console.log('[Proctor-Admin] ICE state:', pc.iceConnectionState);
    pc.onconnectionstatechange = () => console.log('[Proctor-Admin] Connection state:', pc.connectionState);

    // Admin only receives — add recvonly transceiver so the offer includes a video m-line
    pc.addTransceiver('video', { direction: 'recvonly' });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    console.log('[Proctor-Admin] Offer created, sending to evaluator user_id:', user_id, 'from_admin_id:', this.admin_user_id);
    this.ws?.send(JSON.stringify({ type: 'offer', target_user_id: user_id, sdp: offer, from_admin_id: this.admin_user_id }));
  }

  async handle_answer(sdp: any) {
    console.log('[Proctor-Admin] handle_answer called');
    if (this.peer_connection) {
      await this.peer_connection.setRemoteDescription(new RTCSessionDescription(sdp));
      console.log('[Proctor-Admin] Remote description set successfully');
    }
  }

  async request_carousel_streams() {
    for (const item of this.carousel_items) {
      const user_id = this.get_carousel_user_id(item);
      if (!user_id || !this.ws) continue;

      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      this.carousel_peer_connections.set(user_id, pc);

      pc.ontrack = (event) => {
        if (event.streams && event.streams[0]) {
          const videoEl = document.getElementById('carousel-video-' + user_id) as HTMLVideoElement;
          if (videoEl) videoEl.srcObject = event.streams[0];
        }
      };

      pc.onicecandidate = (e) => {
        if (e.candidate) {
          this.ws?.send(JSON.stringify({
            type: 'ice_candidate',
            candidate: e.candidate,
            target_type: 'evaluator',
            target_user_id: user_id,
            from_user_id: this.admin_user_id
          }));
        }
      };

      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.ws?.send(JSON.stringify({
        type: 'offer',
        target_user_id: user_id,
        sdp: offer,
        from_admin_id: this.admin_user_id
      }));
    }
  }

  async load_detail() {
    try {
      const res: any = await this.lps.get_evaluator_detail(this.evaluator_id);
      this.evaluator = res?.data || res;
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to load evaluator details', 'Error', 'error');
    }
    try {
      const sessions: any = await this.lps.get_active_sessions({});
      this.carousel_items = (sessions?.data || []).filter((s: any) => (s.user_id || s.User?.id) !== this.evaluator_id);
    } catch { }
  }

  async load_tab_data() {
    if (this.selected_tab === 'ai' && this.ai_triggers.length === 0) {
      try {
        const res: any = await this.lps.get_ai_triggers(this.evaluator_id);
        const raw = res?.data?.rows || res?.data?.data || res?.data || res || [];
        const arr: any[] = Array.isArray(raw) ? raw : [];
        this.ai_triggers = arr.map((t: any) => ({
          ...t,
          trigger_type: t.trigger_type || t.type || this.derive_trigger_type(t),
          image_url: t.image_url || t.image_path || t.captured_image || '',
          triggered_at: t.triggered_at || t.captured_at || t.created_at || t.createdAt || '',
        }));
        this.cdr.detectChanges();
      } catch (e: any) {
        this.gs.toastr_shows_function(e?.error?.message || 'Failed to load AI triggers', 'Error', 'error');
      }
    } else if (this.selected_tab === 'activity' && this.activity_logs.length === 0) {
      try {
        const res: any = await this.lps.get_proctor_activity(this.evaluator_id);
        const all = res?.data || res || [];
        // Exclude chat messages — only keep system/AI activity events
        this.activity_logs = all.filter((log: any) =>
          log.event_type !== 'proctor_message' &&
          log.event_type !== 'message' &&
          !log.sender_type &&
          !log.to_user_id
        );
      } catch (e: any) {
        this.gs.toastr_shows_function(e?.error?.message || 'Failed to load activity log', 'Error', 'error');
      }
    } else if (this.selected_tab === 'messenger' && this.messages.length === 0) {
      try {
        const res: any = await this.lps.get_proctor_messages(this.evaluator_id);
        const raw: any[] = res?.data || res || [];
        this.messages = raw.map((m: any) => ({
          ...m,
          sender_type: m.sender_type || (
            (m.from === 'admin' || m.from === 'proctor' || Number(m.from_user_id) === this.admin_user_id)
              ? 'admin'
              : 'evaluator'
          ),
        }));
      } catch (e: any) {
        this.gs.toastr_shows_function(e?.error?.message || 'Failed to load messages', 'Error', 'error');
      }
    }
  }

  async select_tab(tab: 'info' | 'ai' | 'activity' | 'messenger') {
    this.selected_tab = tab;
    await this.load_tab_data();
  }

  async send_message() {
    const msg = this.new_message.trim();
    if (!msg) return;
    try {
      await this.lps.send_proctor_message(this.evaluator_id, msg);
      this.messages.push({ message: msg, sender_type: 'admin', created_at: new Date().toISOString() });
      this.new_message = '';
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to send message', 'Error', 'error');
    }
  }

  navigate_to_evaluator(user_id: number) {
    this.router.navigate(['/ai-admin/live-proctor', user_id]);
  }

  format_date(date: string): string {
    if (!date) return '-';
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const h12 = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'pm' : 'am';
    return `${dd}/${mm}/${yyyy} ${h12}:${min}${ampm}`;
  }

  private readonly TRIGGER_LABELS: Record<string, string> = {
    face_not_matched: 'FACE NOT MATCHED',
    no_face_detected: 'NO FACE DETECTED',
    multiple_face_detected: 'MULTIPLE FACE DETECTED',
  };

  private derive_trigger_type(record: any): string {
    if (record.face_verified === null || record.face_verified === undefined) return 'no_face_detected';
    if (record.verification_result) {
      try {
        const vr = typeof record.verification_result === 'string'
          ? JSON.parse(record.verification_result)
          : record.verification_result;
        const msg = (vr.message || '').toLowerCase();
        if (msg.includes('no face') || msg.includes('not detect') || msg.includes('face not found')) return 'no_face_detected';
        if (msg.includes('multiple') || msg.includes('more than one')) return 'multiple_face_detected';
      } catch { }
    }
    return record.face_verified === false ? 'face_not_matched' : '';
  }

  get_trigger_label(trigger: any): string {
    if (trigger.trigger_label) return String(trigger.trigger_label).toUpperCase();
    const type = trigger.trigger_type || trigger.type || this.derive_trigger_type(trigger);
    return this.TRIGGER_LABELS[type] || (trigger.label ? String(trigger.label).toUpperCase() : 'AI ALERT');
  }

  format_time(date: string): string {
    if (!date) return '-';
    const d = new Date(date);
    const h12 = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'AM' : 'PM';
    return `${h12}:${min} ${ampm}`;
  }

  get_image_url(path: string): string {
    if (!path) return '';
    return path.startsWith('http') ? path : `${this.hs.base_url}${path}`;
  }

  get_avatar_initial(): string {
    const name = this.evaluator?.User?.name || '';
    return name.charAt(0).toUpperCase() || 'U';
  }

  open_fullscreen() {
    this.is_fullscreen = true;
    this.stream_set_on_full = false;
  }

  close_fullscreen() {
    this.is_fullscreen = false;
  }

  download_ai_triggers() {
    this.gs.toastr_shows_function('Download functionality coming soon', 'Info', 'success');
  }

  download_activity_log() {
    this.gs.toastr_shows_function('Download functionality coming soon', 'Info', 'success');
  }

  scroll_carousel(direction: number) {
    if (this.carouselEl?.nativeElement) {
      this.carouselEl.nativeElement.scrollLeft += direction * 200;
    }
  }

  get_carousel_user_id(item: any): number {
    return item.user_id || item.User?.id || 0;
  }

  get_carousel_name(item: any): string {
    return item.User?.name || item.name || '-';
  }
}
